/* eslint-disable react-hooks/exhaustive-deps */
import { TextSectionsLengths } from '@airimpact/types/enums'
import constate from 'constate'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { IExtentGeomety } from './useArcgisMap'
import { IIcon, IProject, useProjectsContext } from './useProjects'
import { userUiAdviserContext } from './useUiAdviser'
import { IFile, useWorkspaceContext } from './useWorkspace'
import apiClient from '../api/apiClient'

interface IImg {
  src: string
  alt?: string | null
  imageFile?: File | null
}

export interface IProjectLocation {
  id: string
  place: { name: string }
  displayName: string
  featureId: string
  coordinates: {
    lat: number
    lng: number
  }
}
export interface MapState {
  zoomLevel: number | null
  center: { longitude: number; latitude: number } | null
  extent?: IExtentGeomety | null
}
// TODO group this better
export const useProjectsDetails = () => {
  const [isProjectDetailsLoading, setIsProjectDetailsLoading] = useState(true)

  const [fieldsValidations, setFieldValidations] = useState({
    projectName: false,
    projectDescription: false,
    displayName: false,
    startDate: false,
    endDate: false,
  })

  // qunestionnaire answers
  const [projectActivities, setProjectActivities] = useState<string[]>([])
  const [certificationStandard, setCertificationStandard] = useState<string>('')
  const [methodology, setMethodology] = useState<string>('')
  const [jointCertification, setJointCertification] = useState<string[]>([])
  const [isApplyingJointCertification, setIsApplyingJointCertification] = useState<string>('')
  const [groupedProjects, setGroupedProjects] = useState<string>('')
  const [projectCountry, setProjectCountry] = useState<string>('')
  const [multipleLocations, setMultipleLocations] = useState<string>('')

  const [selectedProject, setSelectedProject] = useState<IProject>()

  const [description, setDescription] = useState(selectedProject?.description)
  const [projectStatus, setProjectStatus] = useState(selectedProject?.status)

  // location
  const [projectLocation, setProjectLocation] = useState<IProjectLocation[]>([])
  const [initialProjectLocations, setInitialProjectLocations] = useState<IProjectLocation[]>([])

  const [defaultProjectLocation, setDefaultProjectLocation] = useState<IProjectLocation>()
  const [mapStateSettings, setMapStateSettings] = useState<MapState>()

  const [projectManager, setProjectManager] = useState<string | undefined>('')
  const [img, setImg] = useState<IImg>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [story, setStory] = useState('')
  const [thumbnail, setThumbnail] = useState<IFile | null>()

  const [metricValue, setMetricValue] = useState('')
  const [icon, setIcon] = useState<IIcon>()

  const [metricUnit, setMetricUnit] = useState('')
  const [metricAction, setMetricAction] = useState('')
  const [projectName, setProjectName] = useState<string | undefined>('')

  const { projectId } = useParams<any>()

  const { updateProject } = useProjectsContext()
  const { selectedNodeId, uploadFile, richTextEditorUpload } = useWorkspaceContext()
  const { questionnaire, postChatQuestionnaireAnswers, fetchQuestionnaireOnly } = userUiAdviserContext()
  const { projects } = useProjectsContext()

  const getProjectById = async (projectId: string) => {
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/project/${projectId}`)

    return {
      ...data.data,
      id: data.data._id,
      thumbnail: {
        id: data.data._pictureId,
        url: data.data.picture?.url,
      },
    }
  }

  const getGeoData = async (projectId: string) => {
    const response = await apiClient.get(
      `/integrations/arcgis/workspace/${selectedNodeId}/project/${projectId}/geodata`,
    )
    const geodata = await Promise.all(
      response.data.geodata
        .filter((geodata: any) => geodata.status !== 'archived' && geodata.type === 'location')
        .map(async (geodata: any) => {
          const jsonData = await apiClient.get(geodata.file[0].url, { withCredentials: false })
          const { features } = jsonData.data
          return {
            id: geodata._id,
            featureId: features[0].properties._id,
            place: {
              name: features[0].properties.name,
            },
            displayName: features[0].properties.displayName,
            coordinates: {
              lat: features[0].geometry.coordinates[1],
              lng: features[0].geometry.coordinates[0],
            },
          }
        }),
    )

    return geodata
  }

  const fetchProjectLocation = async (projectId: string) => {
    const projectLocations = await getGeoData(projectId)
    return projectLocations || []
  }

  const fetchProjectDetails = async () => {
    if (!projectId) return
    const project = await getProjectById(projectId)
    const projectLocation = await fetchProjectLocation(projectId)
    if (projectLocation.length === 0) {
      setProjectLocation([
        {
          id: moment().format('x'),
          featureId: '',
          place: { name: '' },
          displayName: '',
          coordinates: {
            lat: 0,
            lng: 0,
          },
        },
      ])
    } else {
      setProjectLocation(projectLocation)
      setInitialProjectLocations(projectLocation)
      setDefaultProjectLocation(projectLocation[0])
    }

    setSelectedProject(project)
    setDescription(project?.description)
    setProjectStatus(project?.state)
    setStartDate(project?.projectDates?.startDate || null)
    setEndDate(project?.projectDates?.endDate || null)
    setStory(project?.story || '')
    setMetricValue(project?.metric?.value || '')
    setMetricUnit(project?.metric?.unit || '')
    setMetricAction(project?.metric?.verb || '')
    setThumbnail(project?.thumbnail)
    setIcon(project?.icon)
    setProjectName(project?.name)
    setProjectManager(project?.project_manager)
    setIsProjectDetailsLoading(false)
    setMapStateSettings(project.mapView)
  }
  useEffect(() => {
    if (projectId && selectedNodeId) {
      fetchProjectDetails()
    }
  }, [selectedNodeId, projectId, projects])

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImg({
        src: URL.createObjectURL(e.target.files[0]),
        imageFile: e.target.files[0],
        alt: e.target.files[0].name,
      })
    }
  }

  const createProjectLocation = async (location: IProjectLocation) => {
    const response = await apiClient.post(
      `/integrations/arcgis/workspace/${selectedNodeId}/project/${projectId}/geodata`,
      {
        name: location.displayName,
        displayName: location.place.name,
        type: 'location',
        geodata: {
          features: [
            {
              type: 'Point',
              coordinates: [location.coordinates.lng, location.coordinates.lat],
              properties: {
                name: location.place.name,
                displayName: location.displayName,
              },
            },
          ],
          properties: {},
        },
      },
    )
    return response.data.geodata._id
  }

  const updateProjectLocation = async (location: IProjectLocation) => {
    const response = await apiClient.patch(
      `/integrations/arcgis/workspace/${selectedNodeId}/project/${projectId}/geodata/${location.id}`,
      {
        name: location.displayName,
        displayName: location.place.name,
        type: 'location',
        geodata: {
          features: [
            {
              featureId: location.featureId,
              type: 'Point',
              operation: 'update',
              coordinates: [location.coordinates.lng, location.coordinates.lat],
              properties: {
                name: location.place.name,
                displayName: location.displayName,
              },
            },
          ],
          properties: {},
        },
      },
    )
    return response.data.geodata._id
  }
  const deleteProjectZone = async (plotId: string) => {
    await apiClient.delete(`/integrations/arcgis/workspace/${selectedNodeId}/project/${projectId}/geodata/${plotId}`)
  }
  const handleProjectLocationSave = async () => {
    const idsToSend = []
    const validProjectLocations = projectLocation.filter(
      (location) => location.place.name !== '' && location.displayName !== '',
    )

    const newItems = validProjectLocations.filter(
      (newItem) => !initialProjectLocations.some((initialItem) => initialItem.id === newItem.id),
    )

    const deletedItems = initialProjectLocations.filter(
      (initialItem) => !validProjectLocations.some((newItem) => newItem.id === initialItem.id),
    )

    const updatedItems = validProjectLocations.filter((newItem) => {
      const initialItem = initialProjectLocations.find((item) => item.id === newItem.id)
      return initialItem && JSON.stringify(initialItem) !== JSON.stringify(newItem)
    })

    const unchangedItems = validProjectLocations.filter((newItem) => {
      const initialItem = initialProjectLocations.find((item) => item.id === newItem.id)
      return initialItem && JSON.stringify(initialItem) === JSON.stringify(newItem)
    })

    const createIds = await Promise.all(newItems.map((item) => createProjectLocation(item)))
    const updateIds = await Promise.all(updatedItems.map((item) => updateProjectLocation(item)))
    await Promise.all(deletedItems.map((item) => deleteProjectZone(item.id)))

    idsToSend.push(...createIds, ...updateIds, ...unchangedItems.map((item) => item.id))
    return idsToSend
  }
  const updateProjectStory = async (story: string) => {
    const projectStory = await richTextEditorUpload(story || '')
    await updateProject({
      id: projectId,

      story: projectStory,
    })
    await fetchProjectDetails()
  }
  const onUpdateClick = async (status?: string) => {
    let _pictureId = null
    if (img && img.imageFile) {
      const file = await uploadFile(img.imageFile, selectedNodeId)
      _pictureId = file?.id
    } else if (thumbnail) {
      _pictureId = thumbnail.id
    }
    const _locationId = await handleProjectLocationSave()

    const response = await updateProject({
      id: projectId,
      description,
      status,
      _pictureId,
      icon,
      project_manager: projectManager,
      state: projectStatus,
      _locationId,
      name: projectName,
      projectDates: {
        startDate,
        endDate,
      },
      metric: {
        value: String(metricValue),
        unit: String(metricUnit),
        verb: String(metricAction),
      },
    })
    const questionnaireAnswers = [
      {
        id: 2,
        answer: projectActivities,
      },
      {
        id: 4,
        answer: certificationStandard || null,
      },
      {
        id: 5,
        answer: isApplyingJointCertification,
      },
      {
        id: 6,
        answer:
          isApplyingJointCertification === 'Yes' ? (jointCertification.length > 0 ? jointCertification : null) : null,
      },
      {
        id: 7,
        answer: methodology || null,
      },
      {
        id: 9,
        answer: multipleLocations,
      },
      { id: 10, answer: groupedProjects },
    ].filter((questionnaire) => questionnaire.answer !== undefined && questionnaire.answer !== '')

    await postChatQuestionnaireAnswers(questionnaireAnswers, certificationStandard)
    await fetchProjectDetails()
    return response.project
  }

  useEffect(() => {
    if (questionnaire) {
      const answers = questionnaire?.answers || []
      const activitiesAnswer = answers.find((answer) => answer.id === 2)
      const projectCountry = answers.find((answer) => answer.id === 1)

      const certificationStandard = answers.find((answer) => answer.id === 4)
      const methodology = answers.find((answer) => answer.id === 7)

      const isApplyingJointCertification = answers.find((answer) => answer.id === 5)

      const multipleLocations = answers.find((answer) => answer.id === 19)

      const jointCertifications = answers.find((answer) => answer.id === 6)
      const groupedProjects = answers.find((answer) => answer.id === 10)

      const activities = (activitiesAnswer?.answer as string[]) || []
      setProjectActivities(activities)
      setCertificationStandard(certificationStandard?.answer as string)
      setMethodology(methodology?.answer as string)
      setJointCertification((jointCertifications?.answer as string[]) || [])
      setGroupedProjects(groupedProjects?.answer as string)
      setProjectCountry(projectCountry?.answer as string)
      setMultipleLocations(multipleLocations?.answer as string)
      setIsApplyingJointCertification(isApplyingJointCertification?.answer as string)
    }
  }, [questionnaire])

  const fetchMethodologies = async () => {
    await fetchQuestionnaireOnly(certificationStandard)
  }
  useEffect(() => {
    if (!certificationStandard) return
    fetchMethodologies()
  }, [certificationStandard])
  const onIsValidChange = (name: string, isValid: boolean) => {
    setFieldValidations((previousValidations) => ({ ...previousValidations, [name]: isValid }))
  }

  const isValidForm = Boolean(
    useMemo(() => {
      const isAllFieldsValid = Object.values(fieldsValidations).every((isValid) => isValid)
      if (selectedProject?.status) {
        return (
          isAllFieldsValid &&
          projectLocation?.length > 0 &&
          (!description || description?.length <= TextSectionsLengths.short) &&
          projectLocation.every((location) => location.displayName.length <= TextSectionsLengths.name)
        )
      }
      return isAllFieldsValid
    }, [fieldsValidations, selectedProject, projectLocation, description]),
  )

  const onProjectLocationChange = (id: string, partialLocation: Partial<IProjectLocation>) => {
    const newProjectLocation = projectLocation.map((location) => {
      if (location.id === id) {
        return {
          ...location,
          ...partialLocation,
        }
      }
      return location
    })
    setProjectLocation(newProjectLocation)
  }
  const onDeleteProjectLocation = (id: string) => {
    const newProjectLocation = projectLocation.filter((location) => location.id !== id)
    if (newProjectLocation.length === 1) {
      setMultipleLocations('No')
    }
    setProjectLocation(newProjectLocation)
  }

  const onAddProjectLocation = () => {
    const id = moment().format('x')
    const newProjectLocation = [
      ...projectLocation,
      {
        id,
        featureId: '',
        place: { name: '' },
        displayName: '',
        coordinates: {
          lat: 0,
          lng: 0,
        },
      },
    ]
    if (newProjectLocation.length > 1) {
      setMultipleLocations('Yes')
    }
    setProjectLocation(newProjectLocation)
  }
  const updateProjectAreaMapState = async (mapStateDetails: MapState) => {
    const { data } = await apiClient.patch(`/workspace/${selectedNodeId}/project/${projectId}`, {
      mapView: {
        zoomLevel: mapStateDetails.zoomLevel,
        center: {
          longitude: mapStateDetails.center?.longitude,
          latitude: mapStateDetails.center?.latitude,
        },
        extent: {
          xmin: mapStateDetails?.extent?.xmin || 0,
          ymin: mapStateDetails?.extent?.ymin || 0,
          xmax: mapStateDetails?.extent?.xmax || 0,
          ymax: mapStateDetails?.extent?.ymax || 0,
          // spatialReference: {
          //   wkid: 4326,
          // },
        },
      },
    })
    await fetchProjectDetails()
    return data
  }
  return {
    onAddProjectLocation,
    onDeleteProjectLocation,
    onProjectLocationChange,
    selectedProject,
    projectId,
    description,
    setDescription,
    projectStatus,
    setProjectStatus,
    onUpdateClick,
    onImageChange,
    img,
    setImg,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    story,
    setStory,
    metricValue,
    setMetricValue,
    metricUnit,
    setMetricUnit,
    metricAction,
    setMetricAction,
    thumbnail,
    setThumbnail,
    icon,
    setIcon,
    projectLocation,
    projectManager,
    setProjectManager,
    projectName,
    setProjectName,
    fetchProjectDetails,
    fieldsValidations,
    setFieldValidations,
    onIsValidChange,
    isValidForm,
    projectActivities,
    setProjectActivities,
    certificationStandard,
    setCertificationStandard,
    jointCertification,
    setJointCertification,
    groupedProjects,
    setGroupedProjects,
    isApplyingJointCertification,
    setIsApplyingJointCertification,
    methodology,
    setMethodology,
    projectCountry,
    defaultProjectLocation,
    isProjectDetailsLoading,
    updateProjectStory,
    updateProjectAreaMapState,
    mapStateSettings,
  }
}

const [ProjectDetailsContainer, useProjectDetailsContext] = constate(useProjectsDetails)

export { ProjectDetailsContainer, useProjectDetailsContext }
